import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { TaapProposal } from '../../types/taap';
import { TaskySelect } from '../tasky';

const taapIdeaUrl = (path: '/ads/banner' | '/ads/video') => (proposalId: string, productId: number, ideaId: string, adAccountId?: number | null): string => {
  const params = new URLSearchParams({ taap_proposal: proposalId, product: String(productId), idea: ideaId });
  if (adAccountId && Number.isSafeInteger(adAccountId) && adAccountId > 0) params.set('account', String(adAccountId));
  return `${path}?${params.toString()}`;
};

export const taapBannerIdeaUrl = taapIdeaUrl('/ads/banner');
export const taapVideoIdeaUrl = taapIdeaUrl('/ads/video');

const TaapProposalBannerIdeas: React.FC<{ proposal: TaapProposal }> = ({ proposal }) => {
  const strategies = proposal.product_strategies ?? [];
  const [selectedProductId, setSelectedProductId] = useState('');
  const selected = strategies.length === 1
    ? strategies[0]
    : strategies.find((product) => String(product.product_id) === selectedProductId);

  if (!['creative_refresh', 'appeal_change'].includes(proposal.type)) return null;

  return (
    <section className="mt-4 rounded-xl border border-gray-200 bg-white p-5" aria-labelledby="banner-ideas-heading">
      <h2 id="banner-ideas-heading" className="text-sm font-semibold text-gray-900">商品戦略からバナー・動画を作成</h2>
      <p className="mt-1 text-xs leading-relaxed text-gray-500">
        訴求ごとの効果は未検証です。
      </p>
      {strategies.length === 0 ? (
        <p className="mt-3 text-sm text-gray-600">
          この広告アカウントには商品が紐づいていません。
          <Link className="ml-1 text-blue-700 underline" to="/knowledge">商品情報で広告アカウントを紐づける</Link>
        </p>
      ) : (
        <>
          {strategies.length > 1 ? (
            <label className="mt-3 block text-sm text-gray-700">
              対象の商品
              <TaskySelect
                className="mt-1"
                value={selectedProductId}
                onChange={(event) => setSelectedProductId(event.target.value)}
              >
                <option value="">広告で紹介する商品を選んでください</option>
                {strategies.map((product) => <option key={product.product_id} value={product.product_id}>{product.product_name}</option>)}
              </TaskySelect>
            </label>
          ) : <p className="mt-3 text-sm font-medium text-gray-800">対象商品: {selected?.product_name}</p>}
          {selected && selected.ideas.length === 0 && (
            <p className="mt-3 text-sm text-gray-600">この商品には訴求候補がまだ登録されていません。商品情報の戦略マップに対象者と訴求を登録してください。</p>
          )}
          {selected && (
            <div className="mt-3 space-y-3">
              {selected.ideas.map((idea) => (
                <article key={idea.id} className="rounded-lg border border-gray-200 p-4">
                  <div className="text-xs text-gray-500">{idea.segment_name}{idea.who_name && idea.who_name !== idea.segment_name ? ` · ${idea.who_name}` : ''}</div>
                  <h3 className="mt-1 text-sm font-semibold text-gray-900">{idea.appeal_name}</h3>
                  {idea.appeal_direction && <p className="mt-1 text-sm text-gray-700">{idea.appeal_direction}</p>}
                  {idea.copy_examples.length > 0 && (
                    <div className="mt-2 rounded-md bg-[#FAF9F5] p-3">
                      <div className="text-xs text-gray-500">登録済みコピー候補</div>
                      <ul className="mt-1 list-inside list-disc text-sm text-gray-800">
                        {idea.copy_examples.map((copy, index) => <li key={index}>{copy}</li>)}
                      </ul>
                    </div>
                  )}
                  <p className="mt-2 text-xs leading-relaxed text-gray-500">{idea.rationale}</p>
                  <p className="mt-1 text-xs text-gray-400">出典: {selected.product_name}の商品戦略</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Link
                      className="inline-flex rounded-lg bg-[#D4FF6F] px-3 py-2 text-sm font-semibold text-gray-900 hover:bg-[#c6f05e]"
                      to={taapBannerIdeaUrl(proposal.id, selected.product_id, idea.id, proposal.ad_account_id)}
                    >この訴求でバナーを作る</Link>
                    <Link
                      className="inline-flex rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-50"
                      to={taapVideoIdeaUrl(proposal.id, selected.product_id, idea.id, proposal.ad_account_id)}
                    >この訴求で動画を作る</Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default TaapProposalBannerIdeas;
